import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { AlertCircle, AlertTriangle, Info, ChevronDown, ChevronRight, RefreshCw, Trash2 } from 'lucide-react';
import { useConfirmation } from '@/components/shared/ZCard';

const APPS = ['all','giraffe','uncode','cult','framed','soulsync','zephyr','dash','verve','arx','system','dashboard','library'];
const SEVERITIES = ['all','error','warning','info'];

const APP_COLORS = {
  giraffe: '#10B981', uncode: '#F59E0B', cult: '#EF4444', framed: '#6B35B8',
  soulsync: '#8B5CF6', zephyr: '#3B82F6', dash: '#06B6D4', verve: '#FF0042',
  arx: '#64748B', system: '#9CA3AF', dashboard: '#6366F1', library: '#A78BFA',
};

const SEV_ICON = {
  error: <AlertCircle size={10} className="text-red-400" />,
  warning: <AlertTriangle size={10} className="text-amber-400" />,
  info: <Info size={10} className="text-blue-400" />,
};

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function LogEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const color = APP_COLORS[entry.app] || '#9CA3AF';

  return (
    <div className="border-b border-white/[0.04] last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start gap-2.5 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="mt-1 shrink-0">{SEV_ICON[entry.severity] || SEV_ICON.error}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-[10px] text-[#b0b4c0] font-mono">{timeAgo(entry.created_at || entry.timestamp)}</span>
            <span
              className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ color, background: `${color}15` }}
            >
              {entry.app}
            </span>
          </div>
          <p className="text-[13px] text-[#f0f0f5] font-mono leading-snug break-all">{entry.message}</p>
        </div>
        <span className="mt-1 shrink-0 text-[#b0b4c0]">
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3 pl-9 space-y-2 text-[11px] font-mono text-[#b0b4c0]">
          {entry.stack && (
            <pre className="whitespace-pre-wrap break-all bg-white/[0.02] rounded p-2 max-h-48 overflow-auto">{entry.stack}</pre>
          )}
          {entry.context && (
            <pre className="whitespace-pre-wrap break-all bg-white/[0.02] rounded p-2">{JSON.stringify(entry.context, null, 2)}</pre>
          )}
          <div className="flex gap-4 text-[10px]">
            <span>URL: {entry.url}</span>
            <span>{entry.created_at || entry.timestamp}</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ErrorLog() {
  const feedback = useConfirmation();
  const [errors, setErrors] = useState([]);
  const [app, setApp] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  const fetchErrors = useCallback(async (before) => {
    try {
      const params = { limit: 100 };
      if (app !== 'all') params.app = app;
      if (severity !== 'all') params.severity = severity;
      if (before) params.before = before;
      const { data } = await api.get('/errors', { params });
      if (before) {
        setErrors(prev => [...prev, ...data.errors]);
      } else {
        setErrors(data.errors);
      }
      setHasMore(data.has_more);
    } catch {
      // don't toast here to avoid infinite loop
    } finally {
      setLoading(false);
    }
  }, [app, severity]);

  useEffect(() => {
    setLoading(true);
    fetchErrors();
  }, [fetchErrors]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchErrors(), 10000);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh, fetchErrors]);

  const handleClear = async () => {
    try {
      await api.delete('/errors');
      setErrors([]);
      feedback.confirm('Error log cleared');
    } catch {
      feedback.fail('Failed to clear errors');
    }
  };

  const loadMore = () => {
    const last = errors[errors.length - 1];
    if (last) fetchErrors(last.created_at || last.timestamp);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl text-[#f0f0f5] font-medium tracking-tight">Error Log</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAutoRefresh(a => !a)}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded transition-colors ${autoRefresh ? 'bg-[#6B35B8]/20 text-[#9B6DD4]' : 'bg-white/[0.03] text-[#b0b4c0]'}`}
          >
            <RefreshCw size={11} className={autoRefresh ? 'animate-spin' : ''} style={autoRefresh ? { animationDuration: '3s' } : {}} />
            Auto
          </button>
          <button
            onClick={handleClear}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded bg-white/[0.03] text-[#b0b4c0] hover:text-red-400 transition-colors"
          >
            <Trash2 size={11} />
            Clear
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <select
          value={app}
          onChange={e => setApp(e.target.value)}
          className="text-xs bg-white/[0.03] border border-white/[0.06] text-[#f0f0f5] rounded px-2.5 py-1.5 outline-none"
        >
          {APPS.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={severity}
          onChange={e => setSeverity(e.target.value)}
          className="text-xs bg-white/[0.03] border border-white/[0.06] text-[#f0f0f5] rounded px-2.5 py-1.5 outline-none"
        >
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg overflow-hidden">
        {loading && errors.length === 0 ? (
          <div className="py-16 text-center text-[#b0b4c0] text-sm">Loading...</div>
        ) : errors.length === 0 ? (
          <div className="py-16 text-center text-[#b0b4c0] text-sm">No errors logged</div>
        ) : (
          errors.map(e => <LogEntry key={e.id} entry={e} />)
        )}
      </div>

      {hasMore && (
        <button
          onClick={loadMore}
          className="mt-4 w-full text-xs text-[#b0b4c0] hover:text-[#f0f0f5] bg-white/[0.02] border border-white/[0.06] rounded-lg py-2.5 transition-colors"
        >
          Load more
        </button>
      )}
    </div>
  );
}
